import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import App from "@/App";
import { SiteGateBoundary } from "@/components/app/SiteGateBoundary";

// CRA's dev-mode error overlay treats every window error as fatal —
// including the harmless "ResizeObserver loop completed with undelivered
// notifications" notice that Radix UI (used by shadcn's Select, Popover,
// DropdownMenu, Tabs, Slider…) emits during normal layout.
//
// Two-layer defence, since CRA 5 / webpack-dev-server 4 have several
// error paths (window.onerror, react-refresh, client-overlay WebSocket)
// and no single addEventListener hook catches all of them:
//
//   1. Patch ResizeObserver at the source: wrap every observer's callback
//      in requestAnimationFrame so the observer never triggers a
//      synchronous notify-loop in the first place. This is the fix
//      recommended by the WICG spec author and used by Radix internally.
//      Kills the warning at the browser level so no overlay ever fires.
//
//   2. Belt-and-braces: capture-phase listeners on `error` and
//      `unhandledrejection` still swallow any stragglers that survive
//      layer 1 (some CRA overlay code paths pre-run before layer 1).
//
// Zero impact on production builds.
if (typeof window !== "undefined" && typeof window.ResizeObserver === "function") {
  const Native = window.ResizeObserver;
  // eslint-disable-next-line no-global-assign
  window.ResizeObserver = class DeferredResizeObserver extends Native {
    constructor(cb) {
      super((entries, observer) => {
        // Defer the callback by one frame so the observer's notify pass
        // finishes before the callback mutates layout again, breaking
        // the loop that emits "undelivered notifications".
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || entries.length === 0) return;
          try { cb(entries, observer); } catch (_) { /* swallow — never crash observers */ }
        });
      });
    }
  };
}

const RESIZE_OBSERVER_LOOP_ERRS = [
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
];
const isRoLoopErr = (msg) =>
  typeof msg === "string" && RESIZE_OBSERVER_LOOP_ERRS.some((m) => msg.includes(m));
window.addEventListener(
  "error",
  (e) => {
    if (isRoLoopErr(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  },
  true,
);
window.addEventListener(
  "unhandledrejection",
  (e) => {
    const msg = e?.reason?.message || String(e?.reason || "");
    if (isRoLoopErr(msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <SiteGateBoundary>
          <App />
        </SiteGateBoundary>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
